import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { getConsent, saveConsent, loadAnalytics, setAnalyticsEnabled, OPEN_EVENT } from "@/lib/consent";

const PUBLIC = (p) => p === "/" || p.startsWith("/c/") || p === "/privacy" || p === "/cookie";
const NO_BANNER = (p) => p.startsWith("/c/");

const Btn = ({ children, onClick, testId, dark }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex-1 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${dark ? "bg-black text-white hover:bg-zinc-800" : "border border-black/15 bg-white text-black hover:bg-zinc-50"}`}>
    {children}
  </button>
);

export const CookieBanner = () => {
  const { pathname } = useLocation();
  const [gaId, setGaId] = useState("");
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const isPublic = PUBLIC(pathname);

  useEffect(() => {
    if (!isPublic) return;
    api.get("/public/settings").then((r) => setGaId(r.data.ga_id || "")).catch(() => {});
  }, [isPublic]);
  useEffect(() => {
    const c = getConsent();
    if (!c && isPublic && !NO_BANNER(pathname)) setOpen(true);
    if (!c && NO_BANNER(pathname)) setOpen(false);
    if (c?.analytics && isPublic) loadAnalytics(gaId);
    setAnalyticsEnabled(isPublic && !!c?.analytics);
  }, [gaId, isPublic, pathname]);
  useEffect(() => {
    const h = () => { setAnalytics(!!getConsent()?.analytics); setCustom(true); setOpen(true); };
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);

  if (!open || !isPublic) return null;
  const decide = (a) => {
    saveConsent(a);
    if (a) loadAnalytics(gaId);
    setOpen(false);
    setCustom(false);
  };
  return (
    <div className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-xl rounded-3xl border border-black/10 bg-white/95 p-5 text-zinc-800 shadow-2xl backdrop-blur-xl sm:p-6" role="dialog" aria-label="Preferenze cookie" data-testid="cookie-banner">
      <button onClick={() => decide(false)} className="absolute right-4 top-4 text-zinc-400 hover:text-black" aria-label="Chiudi e rifiuta" data-testid="cookie-close"><X className="h-4 w-4" /></button>
      <div className="pr-6 text-sm font-semibold">Rispettiamo la tua privacy</div>
      <p className="mt-2 text-[13px] leading-relaxed text-zinc-600">
        Usiamo cookie tecnici necessari al funzionamento del sito e, solo con il tuo consenso, cookie statistici di terze parti (Google Analytics) per capire come viene usato. Puoi cambiare idea in ogni momento da "Preferenze cookie" in fondo alla pagina. <Link to="/cookie" className="underline" data-testid="cookie-banner-policy">Cookie policy</Link> · <Link to="/privacy" className="underline">Privacy</Link>
      </p>
      {custom && (
        <div className="mt-4 space-y-3 rounded-2xl bg-zinc-50 p-4 text-[13px]" data-testid="cookie-custom">
          <div className="flex items-center justify-between gap-4"><div><div className="font-medium">Tecnici</div><div className="text-zinc-500">Necessari, sempre attivi</div></div><Switch checked disabled /></div>
          <div className="flex items-center justify-between gap-4"><div><div className="font-medium">Statistici</div><div className="text-zinc-500">Google Analytics, misurazione anonimizzata</div></div><Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-analytics-switch" /></div>
        </div>
      )}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Btn onClick={() => decide(false)} testId="cookie-reject">Rifiuta</Btn>
        {custom ? <Btn onClick={() => decide(analytics)} testId="cookie-save">Salva scelte</Btn> : <Btn onClick={() => setCustom(true)} testId="cookie-customize">Personalizza</Btn>}
        <Btn onClick={() => decide(true)} testId="cookie-accept" dark>Accetta tutti</Btn>
      </div>
    </div>
  );
};
